import React, { useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { GetAction } from './GetAction'
import { canAccessManage } from '../manage/auth'
import {
  DEFAULT_HEADER_MENU,
  buildMenuTree,
  isExternalUrl,
  menuHref,
  pagesToMenuItems,
} from '../utils/pageMenu'
import { mediaUrl } from '../utils/mediaUrl'
import { AUTH_EVENT, clearSession } from '../utils/sessionAuth'
import ManageTopNav from './ManageTopNav'
import { mockImg } from '../design/mockImg'

const DEFAULT_WEBSITE_NAME = 'श्री अरिहंत दिगंबर जैन ट्रस्ट'

function isActive(href, location) {
  if (!href || href === '#' || isExternalUrl(href)) return false
  const hashAt = href.indexOf('#')
  const path = (hashAt >= 0 ? href.slice(0, hashAt) : href) || '/'
  const hash = hashAt >= 0 ? href.slice(hashAt) : ''
  const norm = (value) => value.replace(/\/$/, '') || '/'
  if (hash) return norm(location.pathname) === norm(path) && location.hash === hash
  return norm(location.pathname) === norm(path)
}

function MenuLink({ item, className, onClick }) {
  const href = menuHref(item.url)
  if (!href || href === '#') {
    return <span className={className}>{item.label}</span>
  }
  if (isExternalUrl(href) || Number(item.open_new_tab) === 1) {
    return (
      <a className={className} href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        {item.label}
      </a>
    )
  }
  return (
    <Link className={className} to={href} onClick={onClick}>
      {item.label}
    </Link>
  )
}

function HeaderMenuItem({ item, location, mobile, onNavigate }) {
  const children = Array.isArray(item.children) ? item.children : []
  const [open, setOpen] = useState(false)
  const href = menuHref(item.url)
  const active =
    isActive(href, location) || children.some((child) => isActive(menuHref(child.url), location))
  const cls = `idx-nav whitespace-nowrap py-1${active ? ' idx-nav-active' : ''}`
  const keyOf = (entry, idx) => entry.menu_item_id || `${entry.label}-${idx}`

  if (!children.length) {
    return (
      <MenuLink
        item={item}
        className={mobile ? `${cls} border-b border-[#f6edd8] py-3` : cls}
        onClick={onNavigate}
      />
    )
  }

  if (mobile) {
    return (
      <div className="border-b border-[#f6edd8] py-2">
        <div className="py-1 font-semibold text-[#7E2730]">{item.label}</div>
        <div className="flex flex-col pl-3">
          {children.map((child, idx) => (
            <MenuLink
              key={keyOf(child, idx)}
              item={child}
              className="py-2 text-[#3A3E44]"
              onClick={onNavigate}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className={`${cls} inline-flex items-center gap-1`} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {item.label}
        <i className={`fa-solid fa-chevron-down text-[9px] text-gold transition ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open ? (
        <ul className="idx-dropdown">
          {children.map((child, idx) => (
            <li key={keyOf(child, idx)}>
              <MenuLink item={child} onClick={() => setOpen(false)} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function Header() {
  const history = useHistory()
  const location = useLocation()
  const [websiteName, setWebsiteName] = useState(DEFAULT_WEBSITE_NAME)
  const [logoPath, setLogoPath] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [userOpen, setUserOpen] = useState(false)
  const [showManage, setShowManage] = useState(false)
  const [navItems, setNavItems] = useState([])
  const userRef = useRef(null)
  const loggedIn = Boolean(sessionStorage.getItem('id') || sessionStorage.id)
  const userName = sessionStorage.getItem('name') || sessionStorage.name || ''

  useEffect(() => {
    const refreshManage = () => setShowManage(canAccessManage())
    refreshManage()
    window.addEventListener(AUTH_EVENT, refreshManage)
    return () => window.removeEventListener(AUTH_EVENT, refreshManage)
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const [menuData, pagesData] = await Promise.all([
        GetAction('get', { location: 'header' }, 'menu/list'),
        GetAction('get', { menu_location: 'header' }, 'pages/pagesList'),
      ])
      if (cancelled) return
      if (
        menuData?.status === 'success' &&
        Array.isArray(menuData.response) &&
        menuData.response.length > 0
      ) {
        setNavItems(buildMenuTree(menuData.response))
        return
      }
      const pages =
        pagesData?.status === 'success' && Array.isArray(pagesData.response)
          ? pagesData.response
          : []
      const pageItems = pagesToMenuItems(pages)
      setNavItems(buildMenuTree(pageItems.length ? pageItems : DEFAULT_HEADER_MENU))
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    GetAction('get', {}, 'settings/get').then((data) => {
      if (data?.status !== 'success' || !data.response) return
      const r = data.response
      if (r.website_name) setWebsiteName(String(r.website_name).trim())
      setLogoPath(mediaUrl(r.logo_path || ''))
    })
  }, [])

  useEffect(() => {
    setMenuOpen(false)
    setUserOpen(false)
  }, [location.pathname, location.hash])

  useEffect(() => {
    const onDoc = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false)
    }
    document.addEventListener('click', onDoc)
    return () => document.removeEventListener('click', onDoc)
  }, [])

  const logout = () => {
    clearSession()
    setUserOpen(false)
    history.push('/Login')
  }

  return (
    <>
      {showManage ? <ManageTopNav /> : null}
      <div className="site-design site-header-wrap sticky top-0 z-50">
        <div className="bg-maroon text-white" role="region" aria-label="सूचना पट्टी">
          <div className="mx-auto flex h-9 max-w-7xl items-center justify-between gap-3 px-4 text-[11px] sm:text-xs">
            <p className="shrink-0 tracking-wide text-[#FFE7A3]">|| जय जिनेंद्र ||</p>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/Contact" className="inline-flex items-center gap-1 hover:text-[#FFE7A3]">
                <i className="fa-solid fa-location-dot text-[#C59B27]" aria-hidden="true" />
                <span className="hidden sm:inline">चिंचवड, पुणे</span>
              </Link>
              <a href="https://www.instagram.com/shri_arihantdigambarjain_trust" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#FFE7A3]">
                <i className="fa-brands fa-instagram" />
              </a>
              <a href="https://www.youtube.com/@arihanttrust" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-[#FFE7A3]">
                <i className="fa-brands fa-youtube" />
              </a>
              {loggedIn ? (
                <div className="relative" ref={userRef}>
                  <button type="button" className="hover:text-[#FFE7A3]" onClick={() => setUserOpen((v) => !v)}>
                    {userName || 'खाता'}
                  </button>
                  {userOpen ? (
                    <ul className="idx-dropdown mt-1 w-48">
                      <li><Link to="/Profile">Profile</Link></li>
                      <li><Link to="/DonationHistory">Donation History</Link></li>
                      <li><Link to="/ChangePassword">Change password</Link></li>
                      {showManage ? <li><Link to="/manage">Manage</Link></li> : null}
                      <li><button type="button" onClick={logout}>Logout</button></li>
                    </ul>
                  ) : null}
                </div>
              ) : (
                <>
                  <Link to="/SignUp" className="hover:text-[#FFE7A3]">Sign Up</Link>
                  <Link to="/Login" className="hover:text-[#FFE7A3]">Login</Link>
                </>
              )}
            </div>
          </div>
        </div>

        <header className="border-b border-[#f3e6c8] bg-white">
          <div className="mx-auto flex max-w-[1360px] items-center justify-between gap-4 px-4 py-2 lg:px-6">
            <Link to="/" className="flex min-w-0 items-center gap-2.5">
              <img
                src={logoPath || mockImg('logo.jpg')}
                alt=""
                className="h-12 w-12 shrink-0 rounded-full border border-gold/50 bg-[#FFF8E8] object-cover object-center"
              />
              <span className="min-w-0 leading-tight">
                <span className="font-heading block text-sm text-maroon sm:text-base xl:text-lg">{websiteName}</span>
                <span className="block text-[11px] text-gold sm:text-xs">चिंचवड, पुणे</span>
              </span>
            </Link>

            <div className="ml-auto flex shrink-0 items-center gap-4 xl:gap-6">
              <nav className="hidden items-center justify-end gap-4 text-[13px] text-[#3A3E44] lg:flex xl:gap-6 xl:text-sm" aria-label="मुख्य मेनू">
                {navItems.map((item, idx) => (
                  <HeaderMenuItem
                    key={item.menu_item_id || `${item.label}-${idx}`}
                    item={item}
                    location={location}
                  />
                ))}
              </nav>
              <div className="flex items-center gap-2">
                <Link
                  to="/Donation"
                  className="inline-flex items-center gap-2 rounded-full bg-[#9E1C16] px-4 py-2 text-sm font-semibold text-white shadow-[0_2px_8px_rgba(158,28,22,.3)] transition hover:bg-[#841612] sm:px-5"
                >
                  <i className="fa-solid fa-heart text-[13px] text-[#F6B04E]" aria-hidden="true" />
                  <span>दान करें</span>
                </Link>
                <button
                  type="button"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full text-maroon lg:hidden"
                  aria-expanded={menuOpen}
                  aria-label={menuOpen ? 'मेनू बंद करें' : 'मेनू खोलें'}
                  onClick={() => setMenuOpen((v) => !v)}
                >
                  <i className={`fa-solid ${menuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} />
                </button>
              </div>
            </div>
          </div>
          {menuOpen ? (
            <nav className="border-t border-[#f3e6c8] bg-white lg:hidden" aria-label="मोबाइल मेनू">
              <div className="mx-auto flex max-w-7xl flex-col px-4 py-2 text-sm">
                {navItems.map((item, idx) => (
                  <HeaderMenuItem
                    key={item.menu_item_id || `${item.label}-${idx}`}
                    item={item}
                    location={location}
                    mobile
                    onNavigate={() => setMenuOpen(false)}
                  />
                ))}
              </div>
            </nav>
          ) : null}
        </header>
      </div>

    </>
  )
}

export function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(15,20,28,.55)] p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  )
}

export function ModalHead({ title, onClose }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <h2 className="text-2xl text-maroon">{title}</h2>
      <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full text-maroon hover:bg-[#F8EFCF]" aria-label="बंद करें" onClick={onClose}>
        <i className="fa-solid fa-xmark" />
      </button>
    </div>
  )
}

export default Header
